import {
  create as createAxios,
  isAxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { camelizeKeys } from 'humps';

import { getConfig } from '@/modules/runtimeConfig';

import { emitUnauthorized } from './auth/authEvents';
import { notifyAuthFailure } from './auth/authFailure';
import { authClient } from './authClient';
import { applyBaseUrl, axiosConfig, camelizeResponse } from './axiosConfig';
import {
  clearStoredAuthTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  getStoredTokenExpires,
  storeAuthTokens,
  type StoredAuthTokens,
} from './tokenStorage';
import type { OnboardingApiClient } from './types/onboardingApiClient';

/**
 * Derived from the port rather than imported, so it cannot drift from it and the port's own
 * `RequestConfig` stays private to that module. Only `normalizeClient` needs to name it.
 */
type RequestConfig = Parameters<OnboardingApiClient['get']>[1];

export type RefreshResponse = StoredAuthTokens;

/**
 * In-flight refresh, so concurrent callers in THIS process share one request. Deliberately
 * the only piece of token state we hold.
 *
 * There is no in-memory copy of the token, refresh token or expiry. Storage is the single
 * source of truth and is read on every use, because **the keys are shared**: onboarding,
 * `@vidahealth/login` and VidaReactNative all read and write `vida_jwt_token`,
 * `vida_refresh_token` and `vida_token_expires` through the same
 * `Platform.OS === 'web' ? sessionStorage : SecureStore` branch. Caching meant a host's
 * refresh was invisible to us for the rest of the session — we would keep presenting a
 * refresh token that had already been rotated away. Reading through costs three
 * sub-millisecond storage reads per request and removes the whole class of divergence.
 *
 * Note this dedupe is process-local by nature: it cannot see a host's refresh loop. That
 * gap is handled where it actually bites, in `performRefresh`'s failure path.
 */
let refreshPromise: Promise<RefreshOutcome> | null = null;

/**
 * The refresh token the server has already refused, kept as its VALUE rather than as a boolean.
 *
 * This is the guard `clearAuthTokens()` used to provide for free. Clearing removed the dead
 * token, so the next request found nothing to refresh with and stopped. Now that the write is
 * the session owner's call, a host that passes no `onAuthFailure` leaves the dead token in
 * storage — and with nothing recording that it is dead, every subsequent request presents it
 * again. That cost TWO `/v1/auth/refresh` POSTs per request for the rest of the session: one
 * from the request interceptor's pre-flight refresh, one from the response interceptor's 401
 * retry, both reading the same refused token straight back out of storage.
 *
 * Storing the value rather than a flag is what keeps this correct under shared keys. There is
 * nothing to invalidate and no event to subscribe to: the moment anybody — a host's refresh, a
 * fresh login, `clearAuthTokens` — writes a different refresh token, the comparison stops
 * matching and we try again. A boolean would need an explicit reset on every one of those
 * paths, and missing one would strand a live session as permanently dead.
 */
let rejectedRefreshToken: string | null = null;

/**
 * The stale access token we have already reported `no-refresh-token` for.
 *
 * Same shape and the same cause as `rejectedRefreshToken`: clearing used to make the branch
 * unreachable on the next request, so the report fired once. Without a latch it fires on every
 * request for the rest of the session — and because `notifyAuthFailure` is awaited inside the
 * request interceptor, a host handler that does real work (a re-auth prompt, a metric, an error
 * report) adds its own latency to each one.
 */
let reportedNoRefreshFor: string | null = null;

/**
 * Why a refresh attempt produced no token — and specifically whether that is evidence the
 * stored session is dead.
 *
 * `null` used to carry both answers, and the response interceptor could only read it as "dead":
 * a 503 from the auth gateway, or a device dropping offline, reported `unauthorized`, which
 * standalone means `clearVidaSession` and — via `emitUnauthorized` → `resetUserData` — a
 * member's draft discarded and a bounce back to `/step/account-creation` over a transient blip.
 * `performRefresh` already drew this distinction internally, in its `rejectedByServer` check;
 * this type is what carries it out to the callers that act on it.
 *
 * - `refreshed` — a new token, already written to storage.
 * - `rejected` — the server answered and refused the token we sent, and nobody else had written
 *   a newer one. The strongest "the session is dead" signal we ever get.
 * - `unavailable` — no answer worth acting on: a 5xx, a network failure or timeout, a missing
 *   `apiBaseUrl`, or a rotation caught mid-write. Says nothing about the session.
 */
type RefreshOutcome =
  | { status: 'refreshed'; tokens: RefreshResponse }
  | { status: 'rejected' }
  | { status: 'unavailable' };

/**
 * Our own client — the one the standalone app runs on, and the default whenever nobody
 * injected another.
 *
 * Named `builtInApiClient` rather than `apiClient` because it is no longer necessarily the
 * client in use: `apiClient` below is the indirection every caller goes through, and this is
 * one of the two things it can resolve to. Everything axios-specific — the interceptors, the
 * token slots, the refresh loop — stays attached here.
 */
export const builtInApiClient: AxiosInstance = createAxios(axiosConfig);

// `axiosConfig` carries no `baseURL` — it is resolved per request so a consumer's
// `config.apiBaseUrl` can land after this module was imported. See `applyBaseUrl`.
builtInApiClient.interceptors.request.use(applyBaseUrl);
builtInApiClient.interceptors.response.use(camelizeResponse);

/**
 * The client a host injected, if any. Published by `OnboardingRoot` during render, before any
 * child can fire a request — see `setApiClient`.
 */
let injectedApiClient: OnboardingApiClient | null = null;

/**
 * Wrap an injected client so its responses meet our conventions.
 *
 * The port promises a transport — `Promise<{ data: T }>` — and says nothing about key casing,
 * so a host can satisfy it exactly and still be wrong. Both real consumers are: login's client
 * does not camelize at all (`humps` is not even a dependency), and VidaReactNative camelizes
 * inside its `get`/`post` *helpers*, not in the axios instance — and those helpers return
 * unwrapped `T`, so the thing that satisfies the port is precisely the thing that does not
 * camelize.
 *
 * Left unnormalized, every one of our 22 api subdomains reads snake_case bodies as camelCase:
 * each field is `undefined`, nothing throws, and eligibility silently reads as a no-match.
 * `tsc` cannot catch it — `{ data: T }` holds either way and `T` is our unvalidated assertion.
 *
 * So normalizing is ours to do, not something to ask hosts for. It also keeps the port honest:
 * a host supplies transport (their base URL, their auth, their SSL pinning, their headers) and
 * we own conventions. `camelizeKeys` is idempotent for our purposes — already-camelCase keys
 * pass through — so this is correct whether or not a host happens to camelize too.
 *
 * Mirrors `camelizeResponse`, which does the same job on `builtInApiClient`.
 */
const normalizeClient = (client: OnboardingApiClient): OnboardingApiClient => {
  const camelize = <T>(response: { data: T }): { data: T } =>
    response.data && typeof response.data === 'object'
      ? { ...response, data: camelizeKeys(response.data) as T }
      : response;

  // Explicit type parameters rather than relying on the contextual signature: implementing a
  // generic method with an arrow in an object literal erases `T` to `unknown` inside the body,
  // which would silently widen every caller's response type.
  return {
    get: <T = unknown>(url: string, config?: RequestConfig) =>
      client.get<T>(url, config).then(camelize),
    post: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) =>
      client.post<T>(url, data, config).then(camelize),
    patch: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) =>
      client.patch<T>(url, data, config).then(camelize),
    put: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) =>
      client.put<T>(url, data, config).then(camelize),
  };
};

/**
 * The unwrapped client as the caller passed it, kept only to recognise a republish of the
 * same object. `injectedApiClient` above is its normalized wrapper.
 */
let publishedClient: OnboardingApiClient | null = null;

/**
 * Publish a host's client. Called by `OnboardingRoot` during render; `undefined` clears it,
 * so the built-in resolves again.
 *
 * Injecting the client is about **transport**, not about the session. VidaReactNative's request
 * interceptor opens with `await sslPinningReady` and refuses to send over an un-pinned
 * connection; it also sets an `Accept-Language` the mid-tier exact-matches and branches on
 * Apigee vs JWT. None of that is reproducible from here, and our own instance would bypass all
 * of it. The token is a separate matter and needs no injection — the three repos share the same
 * storage keys, so `getValidAccessToken` reads the host's session directly.
 *
 * Wrapped once per published client rather than per call, so `resolveClient()` stays
 * allocation-free and the identity is stable.
 *
 * "Once" needs the `publishedClient` guard to be true, because `OnboardingRoot` calls this
 * from its render body: without it, every re-render of the root would re-wrap the same host
 * client and hand out a new object.
 */
export const setApiClient = (client: OnboardingApiClient | undefined): void => {
  const next = client ?? null;
  if (next === publishedClient) return;
  publishedClient = next;
  injectedApiClient = next ? normalizeClient(next) : null;
};

/** Test-only reset — the slot is process-wide and would otherwise leak between tests. */
export const resetApiClient = (): void => {
  publishedClient = null;
  injectedApiClient = null;
};

/**
 * Every request goes through here — the injected client if there is one, ours otherwise.
 *
 * Resolved per call rather than captured at import, for the same reason `baseURL` is: this
 * module is imported long before `OnboardingRoot` renders, so anything captured at import
 * time would freeze the built-in in place no matter what a host passed.
 *
 * A **delegating object rather than a re-export**, which is what keeps all 41 call sites
 * across the 22 `api/` subdomains unchanged — they still write `apiClient.get<T>(…)`. The
 * cost is that the object is not an `AxiosInstance`: `interceptors` and `defaults` are absent
 * by design, since neither is meaningful on a client we may not own. The interceptor
 * registrations above therefore address `builtInApiClient` directly.
 */
/**
 * The annotated return type is load-bearing: without it the expression is a union of an
 * `AxiosInstance` method and an `OnboardingApiClient` one, and TypeScript refuses to call a
 * generic method through a union of incompatible signatures. Narrowing to the port here also
 * states the intent — from this point on, only the contract is visible, never axios.
 */
const resolveClient = (): OnboardingApiClient => injectedApiClient ?? builtInApiClient;

/**
 * Type parameters forwarded explicitly, matching `normalizeClient`. Nothing here casts, so
 * contextual typing was already safe — callers read `T` off the `OnboardingApiClient`
 * annotation either way. Stated anyway so the two implementations of this port in this file
 * read the same, and so the next edit to add a body to one of these lines starts from a
 * signature where `T` is in scope rather than erased to `unknown`.
 */
export const apiClient: OnboardingApiClient = {
  get: <T = unknown>(url: string, config?: RequestConfig) => resolveClient().get<T>(url, config),
  post: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) =>
    resolveClient().post<T>(url, data, config),
  patch: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) =>
    resolveClient().patch<T>(url, data, config),
  put: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) =>
    resolveClient().put<T>(url, data, config),
};

export async function setAuthTokens(tokens: StoredAuthTokens): Promise<void> {
  // Storage only — see `refreshPromise`. `storeAuthTokens` writes the refresh token only when
  // the response carried one, which preserves the previous one on a non-rotating refresh; that
  // is the same semantics the old in-memory `tokens.refresh ?? refreshToken` had.
  await storeAuthTokens(tokens);
  // A new session supersedes both verdicts. Value comparison would mostly handle this on its
  // own — a rotating refresh writes a different token — but a NON-rotating refresh re-writes
  // the same one, and leaving the marker set there would strand a session the server just
  // renewed.
  rejectedRefreshToken = null;
  reportedNoRefreshFor = null;
}

export async function clearAuthTokens(): Promise<void> {
  await clearStoredAuthTokens();
  rejectedRefreshToken = null;
  reportedNoRefreshFor = null;
}

export function getApiErrorStatus(error: unknown): number | undefined {
  return isAxiosError(error) ? error.response?.status : undefined;
}

export function isUnauthorizedError(error: unknown): boolean {
  return getApiErrorStatus(error) === 401;
}

/**
 * Two separate things happen here, and they used to be one.
 *
 * `notifyAuthFailure` asks whoever OWNS the session what should happen to it — the standalone
 * app clears; a host that already owns refresh does whatever it does. `emitUnauthorized` tells
 * OUR OWN state to reset, and always fires: `RulesEngineProvider` has to drop the user object
 * and route back to the entry screen whether or not the stored tokens were destroyed.
 *
 * Fusing them meant every final 401 deleted keys shared with the host app, as a side effect of
 * resetting a React context. See `auth/authFailure.ts`.
 */
async function rejectUnauthorized(error: unknown): Promise<never> {
  await notifyAuthFailure('unauthorized');
  emitUnauthorized();
  return Promise.reject(error);
}

function isTokenFresh(expires: string | null): boolean {
  return expires ? Date.now() < new Date(expires).getTime() - 30_000 : false;
}

export async function getValidAccessToken(): Promise<string | null> {
  // Read through on every call. A host may have rotated these keys since our last request.
  const [accessToken, refreshToken, tokenExpires] = await Promise.all([
    getStoredAccessToken(),
    getStoredRefreshToken(),
    getStoredTokenExpires(),
  ]);

  if (accessToken && isTokenFresh(tokenExpires)) return accessToken;
  if (!refreshToken) {
    // A stale access token with nothing to refresh it. Report it and send the request
    // unauthenticated; whether the dead token is removed is the session owner's call.
    //
    // Latched on the token's value, because nothing here removes it any more: without this the
    // report fires on every request for the rest of the session, awaited in the request
    // interceptor. A different access token appearing in storage is a different failure and
    // reports again.
    if (accessToken && reportedNoRefreshFor !== accessToken) {
      reportedNoRefreshFor = accessToken;
      await notifyAuthFailure('no-refresh-token');
    }
    return null;
  }
  // We presented this exact token and the server refused it, and nothing has written a
  // different one since. Spending it again would repeat the same POST for the same rejection.
  // It was reported once, when it happened.
  if (refreshToken === rejectedRefreshToken) return null;
  const outcome = await refreshOutcomeNow();
  return outcome.status === 'refreshed' ? outcome.tokens.token : null;
}

builtInApiClient.interceptors.request.use(async (config) => {
  const token = await getValidAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  // Matches the login app: signals Apigee to skip the Django legacy session
  // mint when this client doesn't care about the sessionid cookie path.
  // Unset in our env, so this is a no-op today; included for parity so flipping
  // the env var — or a consumer passing `config.skipLegacySession` — works
  // without code changes.
  if (getConfig('skipLegacySession')) {
    config.headers.set('X-Legacy', 'false');
  }
  return config;
});

async function performRefresh(): Promise<RefreshOutcome> {
  // Both keys: the refresh token to spend, and the access token as a baseline. Either one
  // having moved by the time we fail means somebody else refreshed — see the catch.
  const [sent, sentAccessToken] = await Promise.all([
    getStoredRefreshToken(),
    getStoredAccessToken(),
  ]);
  // Nothing to spend. Only reachable as a race — both callers check first — and the absence of
  // a refresh token is not the server refusing one, so it is `unavailable` rather than
  // `rejected`. The stale-access-token case is reported in `getValidAccessToken`.
  if (!sent) return { status: 'unavailable' };
  try {
    const res = await authClient.post<RefreshResponse>('/v1/auth/refresh', { refresh: sent });
    await setAuthTokens(res.data);
    return { status: 'refreshed', tokens: res.data };
  } catch (error) {
    // A failure here means one of several different things, and they need opposite responses.
    //
    // The keys are shared with the host app, and every party computes staleness from the same
    // `vida_token_expires` with the same 30s buffer — so both cross the threshold together and
    // can both read the same refresh token before either POSTs. If the backend rotates on
    // refresh, whoever lands second presents a token the winner already consumed and gets a
    // 401. That is "my refresh token is stale", NOT "the session is dead".
    //
    // Distinguish them by re-reading: storage that no longer matches what we started from
    // means somebody else's refresh landed while ours was in flight. Only report when nothing
    // moved — then nobody else refreshed — AND the server actually answered, which is the
    // second check further down.
    //
    // Standalone the rotation half is a no-op: nothing else writes these keys, so nothing ever
    // moves and we report exactly as before.
    const [current, token, expires] = await Promise.all([
      getStoredRefreshToken(),
      getStoredAccessToken(),
      getStoredTokenExpires(),
    ]);

    // TWO signals, because the writers in this process do not agree on which key marks a
    // refresh:
    //
    //   - The REFRESH token moved. The rotation was written through, as VidaReactNative's
    //     `persistApigeeTokens` does.
    //   - The ACCESS token moved and the refresh token did not. The rotation happened and the
    //     new refresh token was thrown away: `@vidahealth/login`'s `attemptSessionRestore` and
    //     `attemptBiometricSignIn` write only jwt + expires, and VRN's `postRefreshToken` does
    //     the same. Storage is left holding a refresh token the server has already consumed —
    //     the very one we just sent — so the first signal CANNOT fire on these paths. Keying on
    //     the refresh token alone made the adopt branch dead code for three of the host's four
    //     refresh paths, and we would call a live session dead and (standalone) delete it while
    //     the member was signed in to the host app.
    const rotatedRefresh = Boolean(current) && current !== sent;
    const rotatedAccess = Boolean(token) && token !== sentAccessToken;

    if (rotatedRefresh || rotatedAccess) {
      // ADOPT WHAT IS ALREADY THERE — do not POST again. The winner's response has already
      // been written to the shared keys, so a second refresh would spend a token we were
      // handed rather than one we earned, and its own failure path (a timeout, a 5xx, a
      // rotation the winner is still mid-write on) would land us in the `refresh-failed` report
      // below and sign the member out of a session that was valid a moment ago.
      //
      // What we deliberately do NOT do is infer that the set is complete from write order. A
      // key that moved is not a commit marker for the pair beside it, because the writers in
      // this process disagree about ordering too: ours (`storeAuthTokens`) and
      // `@vidahealth/login`'s `storeTokens` both write jwt → refresh → EXPIRES, so a new
      // refresh token is observable while `vida_token_expires` still holds the previous
      // session's value. Only VidaReactNative's `setAuthToken` (`utils/jwt.ts`) writes refresh
      // last — and VRN bundles the login package, so both orders are live in one process.
      //
      // A truthiness check cannot see that, because the stale `expires` is a perfectly good
      // string. So validate rather than infer: adopt only a set whose expiry says it is
      // actually usable. A stale one means we are looking at a half-written pair, and the
      // answer there is to fail THIS attempt without clearing — the next request re-reads and
      // picks up the complete set.
      //
      // `refresh` may legitimately be absent here: on the access-token-only paths above, the
      // key still holds the consumed token, and `RefreshResponse` marks it optional. Only
      // `.token` is ever consumed by our callers.
      return token && expires && isTokenFresh(expires)
        ? { status: 'refreshed', tokens: { token, refresh: current ?? undefined, expires } }
        : { status: 'unavailable' };
    }

    // Nobody else refreshed. Before calling that a dead session, check that the server actually
    // ANSWERED. `refresh-failed` is defined as "we presented the stored refresh token and the
    // server rejected it" (see `AuthFailureReason`), and only a 4xx response is evidence of
    // that. Three things land here without one, and none of them says anything about the token:
    //
    //   - A CONFIG ERROR. `applyBaseUrl` throws `missingApiBaseUrl` from `authClient`'s request
    //     interceptor when no `apiBaseUrl` resolves — and for a host that injected its own
    //     `apiClient`, this is the ONLY code path that ever runs `applyBaseUrl`, since every
    //     other call goes to the host's client. Reported as `refresh-failed`, a forgotten
    //     `config.apiBaseUrl` surfaces as a member signed out ~30s before their token would
    //     have expired, with nothing logged anywhere. That is precisely the failure
    //     `missingApiBaseUrl` was written to make loud.
    //   - A NETWORK FAILURE OR TIMEOUT. Offline, DNS, the 30s axios timeout.
    //   - A 5xx. The gateway is down; our token is fine.
    //
    // All three used to report `refresh-failed`, which standalone means `clearVidaSession` — so
    // a network blip during a refresh deleted a valid session. Returning `null` instead costs
    // nothing: the request goes out unauthenticated, a genuine 401 still reaches
    // `rejectUnauthorized`, and the next call re-reads storage and retries once the transient
    // condition clears.
    //
    // Logged rather than swallowed. The bare `catch {}` this replaced could not name the error
    // even to itself, which is how a missing `apiBaseUrl` became invisible.
    //
    // Read structurally rather than through `isAxiosError`, matching the response interceptor
    // below. A thrown config error and an axios network error both have no `response` at all,
    // which is the distinction that matters — and not depending on axios's brand keeps this
    // correct if `authClient` is ever swapped, the same limitation `isUnauthorizedError`
    // documents in the other direction.
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    const rejectedByServer = typeof status === 'number' && status >= 400 && status < 500;
    if (!rejectedByServer) {
      console.error('Auth: refresh could not be attempted — leaving the session alone', error);
      return { status: 'unavailable' };
    }

    // The server answered and rejected the token we sent, and storage still holds it — nobody
    // else refreshed. This is the strongest "the session is dead" signal we ever get. Even so,
    // destroying it is the session owner's decision, not ours — the keys are shared. See
    // `auth/authFailure.ts`.
    // Recorded BEFORE reporting, so a handler that itself issues a request cannot re-enter this
    // path with the same token. Nothing else needs to happen for it to expire: any write of a
    // different refresh token makes the comparison stop matching.
    rejectedRefreshToken = sent;
    await notifyAuthFailure('refresh-failed');
    return { status: 'rejected' };
  }
}

/**
 * Refresh the access token using the stored refresh token. Concurrent callers
 * share the same in-flight Promise so we don't fire the endpoint twice.
 *
 * The outcome-returning form, for the two interceptor paths that have to tell a refused token
 * apart from an unreachable auth server. `refreshAccessTokenNow` is the public shape.
 */
function refreshOutcomeNow(): Promise<RefreshOutcome> {
  refreshPromise ??= performRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

/**
 * Refresh, reported as the token or nothing.
 *
 * Kept at `RefreshResponse | null` because that is what callers outside this module want: `auth`'s
 * `refreshToken()` throws on a null, and neither it nor the tests care WHY a refresh failed —
 * only the interceptors here do, and they use `refreshOutcomeNow` directly.
 */
export async function refreshAccessTokenNow(): Promise<RefreshResponse | null> {
  const outcome = await refreshOutcomeNow();
  return outcome.status === 'refreshed' ? outcome.tokens : null;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

builtInApiClient.interceptors.response.use(
  (response) => response,
  async (error: { config?: RetriableConfig; response?: { status?: number } }) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url ?? '';

    const isRetriable =
      Boolean(original) &&
      status === 401 &&
      !original?._retry &&
      // Skip auto-refresh on the auth endpoints themselves: a 401 from
      // /login means bad credentials (refresh won't help), and a 401 from
      // /refresh means the refresh token itself is invalid.
      !url.includes('/v1/auth/login') &&
      !url.includes('/v1/auth/refresh');

    // Read through for the refresh token rather than a cached copy — a host may have written a
    // usable one since our last request. Ordered after the cheap checks so a non-401 response
    // never touches storage.
    //
    // A token the server has already refused counts as absent: retrying it fires the second of
    // the two POSTs `rejectedRefreshToken` exists to stop, for the same rejection.
    const stored = isRetriable ? await getStoredRefreshToken() : null;

    if (original && stored && stored !== rejectedRefreshToken) {
      original._retry = true;
      const outcome = await refreshOutcomeNow();
      if (outcome.status === 'refreshed') {
        original.headers.set('Authorization', `Bearer ${outcome.tokens.token}`);
        return builtInApiClient(original);
      }
      // `unavailable` — the auth server never answered, so this 401 is not evidence that the
      // session is dead. Reporting `unauthorized` here is what made a 503 from the gateway
      // (standalone) clear a live session and, through `emitUnauthorized` → `resetUserData`,
      // discard the member's draft and bounce them to `/step/account-creation`. Reject the
      // original error instead and leave everything alone: the next request re-reads storage
      // and refreshes again once the transient condition clears.
      //
      // This is the reactive half of the guard `performRefresh` already applies to the
      // pre-flight refresh. Only `rejected` — the server answering and refusing the token —
      // reaches `rejectUnauthorized` from here.
      if (outcome.status === 'unavailable') return Promise.reject(error);
      return rejectUnauthorized(error);
    }

    if (status === 401) return rejectUnauthorized(error);
    return Promise.reject(error);
  }
);
